"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEDUCTIONS_EXCEED_INVOICE_BALANCE = exports.DEDUCTIONS_EXCEED_UNPAID_BALANCE = void 0;
exports.normalizeRevisionV2Metadata = normalizeRevisionV2Metadata;
exports.submilestoneExceedsParentError = submilestoneExceedsParentError;
exports.submilestoneBelowReleasedError = submilestoneBelowReleasedError;
exports.isIncompleteAddedSubmilestone = isIncompleteAddedSubmilestone;
exports.submilestoneMissingNameError = submilestoneMissingNameError;
exports.submilestoneNegativeAmountError = submilestoneNegativeAmountError;
exports.revisionDiffHasChanges = revisionDiffHasChanges;
exports.buildRevisionDiff = buildRevisionDiff;
exports.escrowReleasedForPaidDiscountCapacity = escrowReleasedForPaidDiscountCapacity;
exports.resolveRevisionDiscountAllocations = resolveRevisionDiscountAllocations;
exports.paidInvoiceCollectedTotal = paidInvoiceCollectedTotal;
exports.validateRevisionDiff = validateRevisionDiff;
exports.applyRevisionDiff = applyRevisionDiff;
const case_workflow_constants_js_1 = require("../case-workflow/case-workflow.constants.js");
const case_workflow_helpers_js_1 = require("../case-workflow/case-workflow.helpers.js");
const round_money_js_1 = require("../money/round-money.js");
const revision_constants_js_1 = require("./revision.constants.js");
const revision_adjustment_code_js_1 = require("./revision-adjustment-code.js");
const revision_discount_allocation_js_1 = require("./revision-discount-allocation.js");
Object.defineProperty(exports, "DEDUCTIONS_EXCEED_INVOICE_BALANCE", { enumerable: true, get: function () { return revision_discount_allocation_js_1.DEDUCTIONS_EXCEED_INVOICE_BALANCE; } });
const revision_gating_js_1 = require("./revision-gating.js");
const revision_live_snapshot_js_1 = require("./revision-live-snapshot.js");
function emptyV2() {
    return {
        schemaVersion: revision_constants_js_1.REVISION_SCHEMA_V2,
        milestoneChanges: [],
        milestonesAdded: [],
        milestonesDeleted: [],
        submilestoneChanges: [],
        submilestonesAdded: [],
        submilestonesDeleted: [],
        invoiceChanges: [],
        adjustmentIds: [],
        adjustmentAllocations: [],
        rounds: [],
    };
}
function objectRows(value) {
    if (!Array.isArray(value))
        return [];
    return value.filter((row) => Boolean(row) && typeof row === "object" && !Array.isArray(row));
}
function normalizeRevisionV2Metadata(raw) {
    const meta = (raw ?? {});
    return {
        ...emptyV2(),
        ...meta,
        schemaVersion: revision_constants_js_1.REVISION_SCHEMA_V2,
        milestoneChanges: objectRows(meta.milestoneChanges),
        milestonesAdded: objectRows(meta.milestonesAdded),
        milestonesDeleted: objectRows(meta.milestonesDeleted),
        submilestoneChanges: objectRows(meta.submilestoneChanges),
        submilestonesAdded: objectRows(meta.submilestonesAdded).filter((item) => !isIncompleteAddedSubmilestone(item)),
        submilestonesDeleted: objectRows(meta.submilestonesDeleted),
        invoiceChanges: objectRows(meta.invoiceChanges),
        adjustmentIds: Array.isArray(meta.adjustmentIds) ? meta.adjustmentIds.filter((id) => typeof id === "string") : [],
        adjustmentAllocations: (0, revision_discount_allocation_js_1.normalizeAdjustmentAllocations)(meta.adjustmentAllocations),
        rounds: objectRows(meta.rounds),
        liveSnapshot: (0, revision_live_snapshot_js_1.isLiveProjectSnapshot)(meta.liveSnapshot) ? meta.liveSnapshot : undefined,
        contractValueBefore: typeof meta.contractValueBefore === "number" ? meta.contractValueBefore : undefined,
        contractValueAfter: typeof meta.contractValueAfter === "number" ? meta.contractValueAfter : undefined,
    };
}
function submilestoneExceedsParentError(milestoneName) {
    return `Submilestone amounts cannot exceed the ${milestoneName.trim() || "milestone"} total. Raise a variation order to increase value.`;
}
function submilestoneBelowReleasedError(milestoneName) {
    return `Submilestone amounts on ${milestoneName.trim() || "milestone"} cannot fall below the amount already released`;
}
function isIncompleteAddedSubmilestone(sub) {
    const amount = Number(sub.amount);
    const hasAmount = Number.isFinite(amount) && amount !== 0;
    return !String(sub.name ?? "").trim() && !String(sub.description ?? "").trim() && !hasAmount;
}
function submilestoneMissingNameError(parentName) {
    const parent = parentName?.trim();
    return parent ? `A submilestone on ${parent} is missing a name` : "A submilestone is missing a name";
}
function submilestoneNegativeAmountError(subName, parentName) {
    const sub = subName?.trim() || "A submilestone";
    const parent = parentName?.trim();
    return parent ? `${sub} on ${parent} cannot be negative` : `${sub} cannot be negative`;
}
function proposedSubmilestoneAllocated(live, meta, parentId) {
    const parent = live.milestones.find((milestone) => milestone.id === parentId);
    const deleted = new Set(meta.submilestonesDeleted.filter((item) => item.parentMilestoneId === parentId).map((item) => item.id));
    let allocated = 0;
    for (const sub of parent?.subMilestones ?? []) {
        if (deleted.has(sub.id))
            continue;
        const change = meta.submilestoneChanges.find((item) => item.id === sub.id);
        allocated += Number(change?.fields.amount ?? (sub.budgetAmount || 0));
    }
    for (const added of meta.submilestonesAdded) {
        if (added.parentMilestoneId !== parentId)
            continue;
        if (isIncompleteAddedSubmilestone(added))
            continue;
        allocated += Number(added.amount || 0);
    }
    return (0, round_money_js_1.roundMoney)(allocated);
}
function revisionDiffHasChanges(meta, live) {
    const startChanged = meta.projectStartDate != null && (!live || (0, revision_gating_js_1.toDateOnly)(meta.projectStartDate) !== (0, revision_gating_js_1.toDateOnly)(live.startDate));
    const endChanged = meta.projectEndDate != null && (!live || (0, revision_gating_js_1.toDateOnly)(meta.projectEndDate) !== (0, revision_gating_js_1.toDateOnly)(live.endDate));
    return (meta.milestoneChanges.length > 0 ||
        meta.milestonesAdded.length > 0 ||
        meta.milestonesDeleted.length > 0 ||
        meta.submilestoneChanges.length > 0 ||
        meta.submilestonesAdded.length > 0 ||
        meta.submilestonesDeleted.length > 0 ||
        meta.invoiceChanges.length > 0 ||
        meta.adjustmentIds.length > 0 ||
        startChanged ||
        endChanged);
}
function buildRevisionDiff(live, proposed) {
    const normalized = normalizeRevisionV2Metadata(proposed);
    const liveStart = (0, revision_gating_js_1.toDateOnly)(live.startDate);
    const liveEnd = (0, revision_gating_js_1.toDateOnly)(live.endDate);
    const nextStart = (0, revision_gating_js_1.toDateOnly)(normalized.projectStartDate);
    const nextEnd = (0, revision_gating_js_1.toDateOnly)(normalized.projectEndDate);
    if (nextStart && nextStart !== liveStart) {
        normalized.projectStartDate = nextStart;
    }
    else {
        delete normalized.projectStartDate;
    }
    if (nextEnd && nextEnd !== liveEnd) {
        normalized.projectEndDate = nextEnd;
    }
    else {
        delete normalized.projectEndDate;
    }
    return normalized;
}
function milestoneAmountPool(live, meta) {
    return [
        ...live.milestones.map((milestone) => ({ id: milestone.id, budgetAmount: Number(milestone.budgetAmount || 0) })),
        ...meta.milestonesAdded.map((row) => ({ id: row.tempId, budgetAmount: Number(row.budgetAmount || 0) })),
    ];
}
function resolveInvoiceAssignmentRows(live, meta) {
    const deletedInvoiceIds = new Set(meta.invoiceChanges.filter((change) => change.delete && change.id).map((change) => change.id));
    const rows = [];
    for (const invoice of live.invoices) {
        if (deletedInvoiceIds.has(invoice.id))
            continue;
        const change = meta.invoiceChanges.find((item) => item.id === invoice.id && !item.delete);
        rows.push({
            id: invoice.id,
            status: invoice.status,
            dueDate: change?.dueDate !== undefined ? change.dueDate : invoice.dueDate,
            milestoneIds: change?.milestoneIds ?? invoice.milestoneIds,
            adjustmentIds: change?.adjustmentIds ?? invoice.adjustmentIds ?? [],
            isNew: false,
        });
    }
    for (const change of meta.invoiceChanges.filter((item) => item.isNew || item.tempId)) {
        rows.push({
            id: change.tempId || `new-invoice-${change.dueDate ?? "undated"}`,
            status: "pending",
            dueDate: change.dueDate,
            milestoneIds: change.milestoneIds ?? [],
            adjustmentIds: change.adjustmentIds ?? [],
            isNew: true,
        });
    }
    return rows;
}
function appendInvoiceUpdatesForAssignedAdjustments(live, meta, invoiceUpdates, invoiceUpdateIds, deletedInvoiceIds) {
    const newlyAcceptedIds = new Set(live.adjustments.filter((adjustment) => meta.adjustmentIds.includes(adjustment.id) && adjustment.status !== "accepted").map((adjustment) => adjustment.id));
    if (newlyAcceptedIds.size === 0)
        return;
    const claimedByInvoiceChanges = new Set(meta.invoiceChanges.flatMap((change) => (change.delete ? [] : change.adjustmentIds ?? [])));
    const pushUpdate = (invoiceId, dueDate, milestoneIds, adjustmentIds) => {
        if (invoiceUpdateIds.has(invoiceId) || deletedInvoiceIds.includes(invoiceId))
            return;
        invoiceUpdates.push({
            id: invoiceId,
            dueDate: dueDate ? (0, revision_gating_js_1.toDateOnly)(dueDate) : null,
            milestoneIds: [...milestoneIds],
            adjustmentIds: [...adjustmentIds],
        });
        invoiceUpdateIds.add(invoiceId);
    };
    for (const row of resolveInvoiceAssignmentRows(live, meta)) {
        if (row.isNew)
            continue;
        const change = meta.invoiceChanges.find((item) => item.id === row.id && !item.delete);
        const assignedNew = row.adjustmentIds.filter((id) => newlyAcceptedIds.has(id));
        const relevant = change?.adjustmentIds ? assignedNew : assignedNew.filter((id) => !claimedByInvoiceChanges.has(id));
        if (relevant.length === 0)
            continue;
        // Paid invoices only accept newly assigned reductions (lower captured / escrow) — HAN-576.
        if (row.status === "paid") {
            const hasReduction = relevant.some((id) => live.adjustments.find((item) => item.id === id)?.kind === "reduction");
            if (!hasReduction)
                continue;
        }
        else if (row.status !== "pending" && row.status !== "draft") {
            continue;
        }
        pushUpdate(row.id, row.dueDate, row.milestoneIds, row.adjustmentIds);
    }
    for (const adjustment of live.adjustments) {
        if (!newlyAcceptedIds.has(adjustment.id) || !adjustment.invoiceId || claimedByInvoiceChanges.has(adjustment.id))
            continue;
        const liveInvoice = live.invoices.find((invoice) => invoice.id === adjustment.invoiceId);
        if (!liveInvoice)
            continue;
        if (liveInvoice.status === "paid") {
            if (adjustment.kind !== "reduction")
                continue;
        }
        else if (liveInvoice.status !== "pending" && liveInvoice.status !== "draft") {
            continue;
        }
        pushUpdate(liveInvoice.id, liveInvoice.dueDate, liveInvoice.milestoneIds, [...new Set([...(liveInvoice.adjustmentIds ?? []), adjustment.id])]);
    }
}
/**
 * Escrow already left a milestone for paid-invoice discount capacity on the parent's invoice.
 * Counts same-invoice sub releases (+ parent drawdown); subs billed on their own invoice (e.g. VO)
 * do not reduce the parent invoice's remaining capacity.
 * With no same-invoice subs: full parent budget when fully released, else parent releasedAmount.
 */
function escrowReleasedForPaidDiscountCapacity(milestone) {
    const parentInvoiceId = milestone.projectInvoiceId ?? null;
    const sameInvoiceSubs = (milestone.subMilestones ?? []).filter((sub) => !sub.projectInvoiceId || sub.projectInvoiceId === parentInvoiceId);
    if (sameInvoiceSubs.length > 0) {
        const fromSubs = sameInvoiceSubs.reduce((sum, sub) => sum + Number(sub.releasedAmount || 0), 0);
        return (0, round_money_js_1.roundMoney)(fromSubs + Number(milestone.drawdownAmount || 0));
    }
    if (milestone.moneyState === "fully_released") {
        return (0, round_money_js_1.roundMoney)(Number(milestone.budgetAmount || 0));
    }
    return (0, round_money_js_1.roundMoney)(Number(milestone.releasedAmount || 0));
}
function resolveRevisionDiscountAllocations(live, meta) {
    const invoiceRows = resolveInvoiceAssignmentRows(live, meta);
    const assignedAdjustmentIds = new Set(invoiceRows.flatMap((row) => row.adjustmentIds));
    const milestones = milestoneAmountPool(live, meta);
    const milestoneById = new Map(live.milestones.map((milestone) => [milestone.id, milestone]));
    // Prefer unpaid/draft capacity; also allow paid/funded remaining escrow so post-fund DOs reduce releasable amount (HAN-576).
    const discountCapacities = invoiceRows
        .filter((row) => row.isNew || row.status === "pending" || row.status === "draft" || row.status === "paid")
        .map((row) => {
        let remaining = (0, revision_discount_allocation_js_1.invoiceRemainingBeforeProjectDiscounts)(milestones, live.adjustments, row.milestoneIds, row.adjustmentIds);
        const paid = row.status === "paid";
        if (paid) {
            const released = row.milestoneIds.reduce((sum, id) => {
                const milestone = milestoneById.get(id);
                return sum + (milestone ? escrowReleasedForPaidDiscountCapacity(milestone) : 0);
            }, 0);
            remaining = Math.max(0, (0, round_money_js_1.roundMoney)(remaining - released));
        }
        return {
            invoiceId: row.id,
            dueDate: row.dueDate,
            remaining,
            paid,
        };
    })
        .filter((row) => row.remaining > 0);
    const ensured = (0, revision_discount_allocation_js_1.ensureProjectDiscountAllocations)(live.adjustments, meta.adjustmentIds, assignedAdjustmentIds, discountCapacities, meta.adjustmentAllocations ?? []);
    return { ...ensured, assignedAdjustmentIds };
}
function paidInvoiceIds(live) {
    return new Set(live.invoices.filter((invoice) => invoice.status !== "pending" && invoice.status !== "draft").map((invoice) => invoice.id));
}
function milestoneById(live) {
    return new Map(live.milestones.map((milestone) => [milestone.id, milestone]));
}
/** @deprecated Prefer DEDUCTIONS_EXCEED_INVOICE_BALANCE — message now covers unpaid + funded capacity. */
exports.DEDUCTIONS_EXCEED_UNPAID_BALANCE = revision_discount_allocation_js_1.DEDUCTIONS_EXCEED_INVOICE_BALANCE;
function paidInvoiceCollectedTotal(live) {
    const paid = paidInvoiceIds(live);
    return live.invoices
        .filter((invoice) => paid.has(invoice.id))
        .reduce((sum, invoice) => {
        return sum + (0, revision_adjustment_code_js_1.sumAssignedInvoiceAmount)(live.milestones, live.adjustments, invoice.milestoneIds, invoice.adjustmentIds ?? []);
    }, 0);
}
function validateRevisionDiff(live, raw, options) {
    const meta = normalizeRevisionV2Metadata(raw);
    const errors = [];
    const milestones = milestoneById(live);
    const paid = paidInvoiceIds(live);
    const isCaseWorkflow = options?.isCaseWorkflow === true;
    if (live.disputing) {
        errors.push("A dispute is open on this project");
    }
    if (!revisionDiffHasChanges(meta, live)) {
        errors.push("Nothing has changed yet");
    }
    if (isCaseWorkflow &&
        (meta.submilestoneChanges.length > 0 || meta.submilestonesAdded.length > 0 || meta.submilestonesDeleted.length > 0)) {
        errors.push("Sub-milestones are not allowed on CaseTrust projects");
    }
    const projectStart = (0, revision_gating_js_1.toDateOnly)(meta.projectStartDate ?? live.startDate);
    const projectEnd = (0, revision_gating_js_1.toDateOnly)(meta.projectEndDate ?? live.endDate);
    for (const change of meta.milestoneChanges) {
        const current = milestones.get(change.id);
        if (!current) {
            errors.push(`Milestone ${change.id} was not found`);
            continue;
        }
        if (change.fields.name != null && (0, revision_gating_js_1.getMilestoneFieldGate)("name", current.moneyState) === "lock") {
            errors.push(`Name is locked on ${current.name}`);
        }
        if (change.fields.name != null && (0, revision_gating_js_1.getMilestoneFieldGate)("name", current.moneyState) === "allow_reason" && !change.reason?.trim()) {
            errors.push(`A reason is required to change the name of ${current.name}`);
        }
        if (change.fields.description !== undefined && (0, revision_gating_js_1.getMilestoneFieldGate)("description", current.moneyState) === "lock") {
            errors.push(`Description is locked on ${current.name}`);
        }
        if (change.fields.description !== undefined && (0, revision_gating_js_1.getMilestoneFieldGate)("description", current.moneyState) === "allow_reason" && !change.reason?.trim()) {
            errors.push(`A reason is required to change the description of ${current.name}`);
        }
        if (change.fields.startDate !== undefined) {
            const gate = (0, revision_gating_js_1.getMilestoneFieldGate)("startDate", current.moneyState);
            if (gate === "lock")
                errors.push(`Start date is locked on ${current.name}`);
            if (gate === "later_only" && (0, revision_gating_js_1.isStartDateLaterOnlyViolation)(current.startDate, change.fields.startDate)) {
                errors.push(`Start date on ${current.name} can only move later`);
            }
        }
        if (change.fields.endDate !== undefined && (0, revision_gating_js_1.getMilestoneFieldGate)("endDate", current.moneyState) === "lock") {
            errors.push(`End date is locked on ${current.name}`);
        }
        const nextStart = (0, revision_gating_js_1.toDateOnly)(change.fields.startDate ?? current.startDate);
        const nextEnd = (0, revision_gating_js_1.toDateOnly)(change.fields.endDate ?? current.endDate);
        if (nextStart && nextEnd && nextEnd < nextStart) {
            errors.push(`${current.name} ends before it starts`);
        }
        if (nextStart && projectStart && nextStart < projectStart) {
            errors.push(`${current.name} starts before the project start date`);
        }
        if (nextEnd && projectEnd && nextEnd > projectEnd) {
            errors.push(`${current.name} ends after the project end date`);
        }
    }
    for (const added of meta.milestonesAdded) {
        if (!added.name?.trim())
            errors.push("A new milestone is missing a name");
        if (!added.reason?.trim())
            errors.push(`A reason is required to add ${added.name || "a milestone"}`);
        if (!(Number(added.budgetAmount) > 0))
            errors.push(`A new milestone needs an amount`);
        const start = (0, revision_gating_js_1.toDateOnly)(added.startDate);
        const end = (0, revision_gating_js_1.toDateOnly)(added.endDate);
        if (start && end && end < start)
            errors.push(`${added.name || "New milestone"} ends before it starts`);
        if (start && projectStart && start < projectStart)
            errors.push(`${added.name || "New milestone"} starts before the project start date`);
        if (end && projectEnd && end > projectEnd)
            errors.push(`${added.name || "New milestone"} ends after the project end date`);
    }
    for (const deleted of meta.milestonesDeleted) {
        const current = milestones.get(deleted.id);
        if (!current) {
            errors.push(`Milestone ${deleted.id} was not found`);
            continue;
        }
        if (isCaseWorkflow && (0, case_workflow_helpers_js_1.isCaseDepositMilestone)(current)) {
            errors.push(case_workflow_constants_js_1.CASE_DEPOSIT_CANNOT_BE_REMOVED);
            continue;
        }
        if ((0, revision_gating_js_1.getMilestoneFieldGate)("delete", current.moneyState) === "lock") {
            errors.push(`${current.name} cannot be deleted because funds are held or released`);
        }
        if (!deleted.reason?.trim()) {
            errors.push(`A reason is required to delete ${current.name}`);
        }
    }
    for (const change of meta.submilestoneChanges) {
        const parent = milestones.get(change.parentMilestoneId);
        if (!parent) {
            errors.push(`Parent milestone ${change.parentMilestoneId} was not found`);
            continue;
        }
        if (parent.moneyState === "fully_released") {
            errors.push(`Submilestones on ${parent.name} are locked`);
        }
        const current = parent.subMilestones?.find((sub) => sub.id === change.id);
        if (!current) {
            errors.push(`Submilestone ${change.id} was not found`);
            continue;
        }
        if (change.fields.amount != null && change.fields.amount < 0) {
            errors.push(submilestoneNegativeAmountError(current.name, parent.name));
        }
        if (change.fields.amount != null && change.fields.amount < Number(current.releasedAmount ?? 0)) {
            errors.push(`Submilestone ${current.name} cannot go below the amount already released`);
        }
    }
    for (const added of meta.submilestonesAdded) {
        if (isIncompleteAddedSubmilestone(added))
            continue;
        const parent = milestones.get(added.parentMilestoneId);
        const addedParent = meta.milestonesAdded.find((item) => item.tempId === added.parentMilestoneId);
        const parentDeleted = meta.milestonesDeleted.some((item) => item.id === added.parentMilestoneId);
        if (parentDeleted) {
            errors.push(`Cannot add a submilestone to a deleted milestone`);
            continue;
        }
        if (!parent && !addedParent) {
            errors.push(`Parent milestone ${added.parentMilestoneId} was not found`);
            continue;
        }
        const parentName = parent?.name ?? addedParent?.name;
        if (!added.name?.trim())
            errors.push(submilestoneMissingNameError(parentName));
        if (Number(added.amount) < 0)
            errors.push(submilestoneNegativeAmountError(added.name, parentName));
    }
    for (const deleted of meta.submilestonesDeleted) {
        const parent = milestones.get(deleted.parentMilestoneId);
        const current = parent?.subMilestones?.find((sub) => sub.id === deleted.id);
        if (current && Number(current.releasedAmount ?? 0) > 0) {
            errors.push(`A released submilestone cannot be deleted`);
        }
    }
    const deletedMilestoneIdsForSubs = new Set(meta.milestonesDeleted.map((item) => item.id));
    const allocationParents = new Map();
    for (const milestone of live.milestones) {
        if (deletedMilestoneIdsForSubs.has(milestone.id))
            continue;
        allocationParents.set(milestone.id, {
            name: milestone.name,
            budget: Number(milestone.budgetAmount || 0),
            released: Number(milestone.releasedAmount || 0),
        });
    }
    for (const added of meta.milestonesAdded) {
        allocationParents.set(added.tempId, { name: added.name, budget: Number(added.budgetAmount || 0), released: 0 });
    }
    for (const [parentId, parent] of allocationParents) {
        const hasSubs = (milestones.get(parentId)?.subMilestones?.length ?? 0) > 0 ||
            meta.submilestoneChanges.some((item) => item.parentMilestoneId === parentId) ||
            meta.submilestonesAdded.some((item) => item.parentMilestoneId === parentId && !isIncompleteAddedSubmilestone(item)) ||
            meta.submilestonesDeleted.some((item) => item.parentMilestoneId === parentId);
        if (!hasSubs)
            continue;
        const allocated = proposedSubmilestoneAllocated(live, meta, parentId);
        if (allocated - 1e-9 > parent.budget)
            errors.push(submilestoneExceedsParentError(parent.name));
        if (parent.released > 0 && allocated + 1e-9 < parent.released)
            errors.push(submilestoneBelowReleasedError(parent.name));
    }
    const deletedMilestoneIds = new Set(meta.milestonesDeleted.map((item) => item.id));
    const addedTempIds = new Set(meta.milestonesAdded.map((item) => item.tempId));
    const remainingLiveIds = live.milestones.filter((milestone) => !deletedMilestoneIds.has(milestone.id)).map((milestone) => milestone.id);
    const requiredIds = new Set([...remainingLiveIds, ...addedTempIds]);
    const assigned = new Map();
    const invoiceChanges = meta.invoiceChanges ?? [];
    const deletedInvoiceIds = new Set(invoiceChanges.filter((change) => change.delete && change.id).map((change) => change.id));
    const milestoneAmounts = milestoneAmountPool(live, meta);
    const allocationResult = resolveRevisionDiscountAllocations(live, meta);
    if (allocationResult.ok === false) {
        errors.push(allocationResult.error);
    }
    const discountPlans = allocationResult.plans;
    for (const invoice of live.invoices) {
        if (deletedInvoiceIds.has(invoice.id)) {
            if (paid.has(invoice.id))
                errors.push(`Paid invoice ${invoice.id} cannot be deleted`);
            continue;
        }
        const change = invoiceChanges.find((item) => item.id === invoice.id && !item.delete);
        const milestoneIds = change?.milestoneIds ?? invoice.milestoneIds;
        const adjustmentIds = change?.adjustmentIds ?? invoice.adjustmentIds ?? [];
        const dueDate = change?.dueDate !== undefined ? change.dueDate : invoice.dueDate;
        if (invoice.status === "pending" && !(0, revision_gating_js_1.toDateOnly)(dueDate ?? null)) {
            errors.push(`Invoice ${invoice.id} is missing a due date`);
        }
        if (isCaseWorkflow && milestoneIds.length > 1) {
            errors.push("CASE workflow requires exactly one milestone per regular invoice");
        }
        for (const milestoneId of milestoneIds) {
            assigned.set(milestoneId, (assigned.get(milestoneId) ?? 0) + 1);
        }
        if (paid.has(invoice.id) && change?.milestoneIds) {
            const original = [...invoice.milestoneIds].sort().join(",");
            const next = [...change.milestoneIds].sort().join(",");
            if (original !== next) {
                errors.push(`Positions cannot be moved out of a paid invoice`);
            }
        }
        if (paid.has(invoice.id) && change?.adjustmentIds) {
            const original = [...(invoice.adjustmentIds ?? [])].sort().join(",");
            const next = [...change.adjustmentIds].sort().join(",");
            if (original !== next) {
                errors.push(`Adjustments cannot be moved on a paid invoice`);
            }
        }
        const invoiceNet = (0, revision_discount_allocation_js_1.invoiceAmountWithDiscountAllocations)(milestoneAmounts, live.adjustments, milestoneIds, adjustmentIds, invoice.id, discountPlans);
        if (invoiceNet < 0) {
            errors.push(`Invoice ${invoice.id} cannot go below zero after deductions`);
        }
    }
    for (const change of invoiceChanges.filter((item) => item.isNew || item.tempId)) {
        if (!(0, revision_gating_js_1.toDateOnly)(change.dueDate ?? null)) {
            errors.push("A new invoice is missing a due date");
        }
        const milestoneIds = change.milestoneIds ?? [];
        const adjustmentIds = change.adjustmentIds ?? [];
        const invoiceId = change.tempId || `new-invoice-${change.dueDate ?? "undated"}`;
        if (isCaseWorkflow && milestoneIds.length > 1) {
            errors.push("CASE workflow requires exactly one milestone per regular invoice");
        }
        for (const milestoneId of milestoneIds) {
            assigned.set(milestoneId, (assigned.get(milestoneId) ?? 0) + 1);
        }
        const invoiceNet = (0, revision_discount_allocation_js_1.invoiceAmountWithDiscountAllocations)(milestoneAmounts, live.adjustments, milestoneIds, adjustmentIds, invoiceId, discountPlans);
        if (invoiceNet < 0) {
            errors.push(`A new invoice cannot go below zero after deductions`);
        }
    }
    for (const id of requiredIds) {
        const count = assigned.get(id) ?? 0;
        if (count === 0)
            errors.push(`Milestone ${id} is not assigned to an invoice`);
        if (count > 1)
            errors.push(`Milestone ${id} is assigned to more than one invoice`);
    }
    for (const adjustmentId of meta.adjustmentIds) {
        const adjustment = live.adjustments.find((item) => item.id === adjustmentId);
        if (!adjustment) {
            errors.push(`Adjustment ${adjustmentId} was not found`);
            continue;
        }
        if (!adjustment.reason?.trim() && adjustment.status !== "accepted") {
            errors.push(`Adjustment ${adjustmentId} needs a reason`);
        }
        if (!(Math.abs(Number(adjustment.amount)) > 0) && adjustment.status !== "accepted") {
            errors.push(`Adjustment ${adjustmentId} needs an amount`);
        }
    }
    const plan = applyRevisionDiff(live, { ...meta, adjustmentAllocations: discountPlans });
    // Compare against paid invoice nets after this revision's deductions (HAN-576), not pre-discount totals.
    const paidCollectedAfterDeductions = live.invoices
        .filter((invoice) => paid.has(invoice.id) && !deletedInvoiceIds.has(invoice.id))
        .reduce((sum, invoice) => {
        const change = invoiceChanges.find((item) => item.id === invoice.id && !item.delete);
        const milestoneIds = change?.milestoneIds ?? invoice.milestoneIds;
        const adjustmentIds = change?.adjustmentIds ?? invoice.adjustmentIds ?? [];
        return (sum +
            Math.max(0, (0, revision_discount_allocation_js_1.invoiceAmountWithDiscountAllocations)(milestoneAmounts, live.adjustments, milestoneIds, adjustmentIds, invoice.id, discountPlans)));
    }, 0);
    if (plan.contractValue + 1e-9 < paidCollectedAfterDeductions) {
        errors.push(exports.DEDUCTIONS_EXCEED_UNPAID_BALANCE);
    }
    return { ok: errors.length === 0, errors };
}
function resolveInvoiceMilestoneIds(live, change, fallback) {
    if (change?.milestoneIds)
        return change.milestoneIds;
    return fallback?.milestoneIds ?? [];
}
function applyRevisionDiff(live, raw) {
    const meta = normalizeRevisionV2Metadata(raw);
    const deletedMilestoneIds = new Set(meta.milestonesDeleted.map((item) => item.id));
    const deletedInvoiceIds = meta.invoiceChanges.filter((change) => change.delete && change.id).map((change) => change.id);
    const remainingMilestones = live.milestones.filter((milestone) => !deletedMilestoneIds.has(milestone.id));
    const remainingBudget = remainingMilestones.reduce((sum, milestone) => sum + Number(milestone.budgetAmount || 0), 0);
    const addedBudget = meta.milestonesAdded.reduce((sum, milestone) => sum + Number(milestone.budgetAmount || 0), 0);
    const existingAccepted = live.adjustments
        .filter((adjustment) => adjustment.status === "accepted")
        .reduce((sum, adjustment) => sum + (0, revision_adjustment_code_js_1.signedAdjustmentAmount)(adjustment), 0);
    const newlyAccepted = live.adjustments
        .filter((adjustment) => meta.adjustmentIds.includes(adjustment.id) && adjustment.status !== "accepted")
        .reduce((sum, adjustment) => sum + (0, revision_adjustment_code_js_1.signedAdjustmentAmount)(adjustment), 0);
    const invoiceUpdates = meta.invoiceChanges
        .filter((change) => change.id && !change.delete && !change.isNew)
        .map((change) => ({
        id: change.id,
        dueDate: change.dueDate,
        milestoneIds: change.milestoneIds,
        adjustmentIds: change.adjustmentIds,
    }));
    const invoiceInserts = meta.invoiceChanges
        .filter((change) => change.isNew || (!change.id && change.tempId))
        .map((change) => ({
        tempId: change.tempId || `new-invoice-${change.dueDate ?? "undated"}`,
        dueDate: change.dueDate,
        milestoneIds: resolveInvoiceMilestoneIds(live, change, undefined),
        adjustmentIds: change.adjustmentIds ?? [],
    }));
    const allocationResult = resolveRevisionDiscountAllocations(live, meta);
    const adjustmentAllocations = allocationResult.ok ? allocationResult.plans : meta.adjustmentAllocations ?? [];
    const invoiceUpdateIds = new Set(invoiceUpdates.map((row) => row.id));
    for (const planRow of adjustmentAllocations) {
        for (const allocation of planRow.allocations) {
            if (invoiceUpdateIds.has(allocation.invoiceId) || deletedInvoiceIds.includes(allocation.invoiceId))
                continue;
            const liveInvoice = live.invoices.find((invoice) => invoice.id === allocation.invoiceId);
            if (!liveInvoice)
                continue;
            invoiceUpdates.push({
                id: allocation.invoiceId,
                dueDate: liveInvoice.dueDate ? (0, revision_gating_js_1.toDateOnly)(liveInvoice.dueDate) : null,
                milestoneIds: [...liveInvoice.milestoneIds],
                adjustmentIds: [...(liveInvoice.adjustmentIds ?? [])],
            });
            invoiceUpdateIds.add(allocation.invoiceId);
        }
    }
    appendInvoiceUpdatesForAssignedAdjustments(live, meta, invoiceUpdates, invoiceUpdateIds, deletedInvoiceIds);
    return {
        project: meta.projectStartDate != null || meta.projectEndDate != null
            ? {
                startDate: meta.projectStartDate ?? undefined,
                endDate: meta.projectEndDate ?? undefined,
            }
            : undefined,
        milestoneUpdates: meta.milestoneChanges.map((change) => ({ id: change.id, fields: change.fields })),
        milestoneInserts: meta.milestonesAdded.map((added) => ({
            tempId: added.tempId,
            name: added.name,
            description: added.description,
            budgetAmount: added.budgetAmount,
            startDate: added.startDate,
            endDate: added.endDate,
        })),
        milestoneDeletes: [...deletedMilestoneIds],
        submilestoneUpdates: meta.submilestoneChanges,
        submilestoneInserts: meta.submilestonesAdded,
        submilestoneDeletes: meta.submilestonesDeleted.map((item) => item.id),
        invoiceUpdates,
        invoiceInserts,
        invoiceDeletes: deletedInvoiceIds,
        adjustmentAcceptIds: [...meta.adjustmentIds],
        adjustmentAllocations,
        contractValue: (0, round_money_js_1.roundMoney)(remainingBudget + addedBudget + existingAccepted + newlyAccepted),
    };
}
